import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	EventEmitter,
	HostBinding,
	OnDestroy,
	OnInit,
	Output,
	ViewEncapsulation,
} from "@angular/core";
import { FormBuilder, FormControl, Validators } from "@angular/forms";
import { ContentServiceValue, ContentValue, Nationality } from "@fabric-types/svc-content";
import { select, Store } from "@ngrx/store";
import { Option } from "@obg/core.select";
import { CommonState, NATIONAL_DOCUMENTS, NationalDocument } from "@obg/ngx.common";
import {
	ConfirmCogaCustomerProfileRequest,
	ConfirmPegaCustomerProfileRequest,
	CORE_ROUTE,
	CUSTOMER_MANAGEMENT_MESSAGE_CONTEXT,
	CustomerManagementAction,
	CustomerManagementSelector,
	MessageService,
	MessageType,
	ProfileNotCompletedData,
	SessionSelector,
} from "@obg/ngx.core";
import { TranslationService } from "@obg/ngx.core.content";
import { DeviceConfigService, DocumentRef, ignoreNil } from "@obg/ngx.global";
import { RouteAction } from "@obg/ngx.router";
import { merge, Observable, Subscription } from "rxjs";
import { filter, map, take, tap } from "rxjs/operators";

import { CONFIRM_PROFILE_ERRORS, CountryCode, DEFAULT_ERROR_KEY } from "./complete-account-login.const";
import { CompleteAccountLoginFormGroup, ComponentState } from "./complete-account-login.container.model";

@Component({
	selector: "obg-m-complete-account-login",
	templateUrl: "./complete-account-login.container.html",
	styleUrls: ["./complete-account-login.container.scss"],
	encapsulation: ViewEncapsulation.None,
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CompleteAccountLoginContainer implements OnInit, OnDestroy {
	@HostBinding("class.obg-m-complete-account-login") hostCssClass = true;

	@Output() close = new EventEmitter<void>();
	@Output() busy = new EventEmitter<boolean>();

	isMobileDevice = !this.deviceConfigService.isDesktop();
	componentState: ComponentState = "success";
	profileNotCompletedData: ProfileNotCompletedData | undefined;
	formGroup: CompleteAccountLoginFormGroup | undefined;
	selectedDocument: NationalDocument | undefined;
	documentTypes: NationalDocument[] = [];
	countries$ = new Observable(this.documentRef.obgEventBus.retrieve(ContentValue.getCountriesByOrder, {})).pipe(
		ignoreNil(),
		map((countries) =>
			countries.map(
				(country) =>
					({
						value: country.code,
						name: country.name,
					}) as Option<string>
			)
		)
	);
	nationalities$: Observable<Nationality[]> | undefined;

	errorCode = DEFAULT_ERROR_KEY;

	private data$$ = new Subscription();
	private authenticationToken: string | undefined;

	constructor(
		private store: Store<CommonState>,
		private changeDetectorRef: ChangeDetectorRef,
		private sessionSelector: SessionSelector,
		private customerManagementAction: CustomerManagementAction,
		private deviceConfigService: DeviceConfigService,
		private formBuilder: FormBuilder,
		private translationService: TranslationService,
		private messageService: MessageService,
		private documentRef: DocumentRef
	) {}

	ngOnInit(): void {
		this.nationalities$ = new Observable(
			this.documentRef.obgEventBus.retrieve(ContentServiceValue.GetAllNationalities)
		).pipe(
			ignoreNil(),
			map((nationalities) =>
				nationalities.map(
					(nationality) =>
						({
							value: nationality.value,
							name: nationality.name,
						}) as Option<string>
				)
			)
		);

		const profileNotCompletedData$ = this.store.pipe(
			select(this.sessionSelector.getProfileNotCompletedData()),
			ignoreNil(),
			take(1),
			tap((profileNotCompletedData) => {
				this.profileNotCompletedData = profileNotCompletedData;
				this.formGroup = this.getFormGroup(profileNotCompletedData);
				this.setupDocumentTypes(profileNotCompletedData);
			})
		);

		const authenticationToken$ = this.store.pipe(
			select(this.sessionSelector.getAuthenticationToken()),
			tap((authenticationToken) => {
				this.authenticationToken = authenticationToken;
			})
		);

		const profileUpdateSuccess$ = this.store.pipe(
			select(CustomerManagementSelector.isProfileUpdateSuccess),
			filter((isSuccess) => !!isSuccess),
			tap(() => {
				this.store.dispatch(RouteAction.navigate(CORE_ROUTE.home));

				this.close.emit();
			})
		);

		const profileUpdateFail$ = this.messageService
			.consume$({
				context: CUSTOMER_MANAGEMENT_MESSAGE_CONTEXT.confirmProfile,
				messageType: MessageType.Error,
			})
			.pipe(
				tap((error) => {
					const code = error[0]?.messageCode;
					this.errorCode = CONFIRM_PROFILE_ERRORS.includes(code)
						? `migrated-user.error.${code?.toLowerCase()}`
						: DEFAULT_ERROR_KEY;
					this.componentState = "error";
					this.busy.emit(false);
				})
			);

		this.data$$.add(
			merge(profileNotCompletedData$, profileUpdateSuccess$, profileUpdateFail$, authenticationToken$).subscribe(
				() => this.changeDetectorRef.markForCheck()
			)
		);
	}

	ngOnDestroy(): void {
		this.data$$?.unsubscribe();
	}

	onSubmit(): void {
		if (!this.profileNotCompletedData?.profileConfirmationToken || !this.formGroup?.valid) {
			this.formGroup?.markAllAsTouched();
			this.componentState = "error";
			this.busy.emit(false);
			return;
		}

		this.busy.emit(true);
		this.componentState = "loading";

		const confirmProfilePayload: ConfirmPegaCustomerProfileRequest | ConfirmCogaCustomerProfileRequest =
			this.getConfirmProfilePayload(this.profileNotCompletedData, this.formGroup);

		this.store.dispatch(this.customerManagementAction.confirmProfile(confirmProfilePayload));
	}

	private getConfirmProfilePayload(
		profileNotCompletedData: ProfileNotCompletedData,
		formGroup: CompleteAccountLoginFormGroup
	): ConfirmPegaCustomerProfileRequest | ConfirmCogaCustomerProfileRequest {
		switch (profileNotCompletedData.countryCode) {
			case CountryCode.PE:
				return {
					profileConfirmationToken: profileNotCompletedData?.profileConfirmationToken,
					authenticationToken: this.authenticationToken,
					...formGroup.value,
				};
			default:
				return {
					token: profileNotCompletedData?.profileConfirmationToken,
					...formGroup.value,
				};
		}
	}

	private onPersonalCodeTypeChange(newDocumentType: string) {
		this.selectedDocument = this.documentTypes.find(({ value }) => value === newDocumentType);

		if (!this.selectedDocument) {
			return;
		}

		this.formGroup?.controls.personalCode?.setValidators(this.selectedDocument.validators);
		this.formGroup?.controls.personalCode?.updateValueAndValidity({ onlySelf: true });
	}

	private setupDocumentTypes(profileNotCompletedData: ProfileNotCompletedData) {
		this.documentTypes =
			NATIONAL_DOCUMENTS[profileNotCompletedData.countryCode.toUpperCase()].map((document) => ({
				...document,
				name: this.translationService.getByKey(document.nameKey),
			})) ?? [];

		const documentTypeChange$$ = this.formGroup?.controls.personalCodeType?.valueChanges
			.pipe(tap((type) => this.onPersonalCodeTypeChange(type)))
			.subscribe(() => this.changeDetectorRef.markForCheck());

		this.data$$.add(documentTypeChange$$);

		this.formGroup?.controls.personalCodeType?.setValue(this.documentTypes[0]?.value);
	}

	private getFormGroup(profileNotCompletedData: ProfileNotCompletedData): CompleteAccountLoginFormGroup {
		switch (profileNotCompletedData.countryCode) {
			case CountryCode.PE:
				return this.formBuilder.group({
					nationality: new FormControl(profileNotCompletedData.countryCode.toUpperCase(), [
						Validators.required,
					]),
					personalCode: new FormControl("", []),
					personalCodeType: new FormControl("", [Validators.required]),
				});
			default:
				return this.formBuilder.group({
					birthCountry: new FormControl(CountryCode.CO, [Validators.required]),
					nationality: new FormControl(CountryCode.CO, [Validators.required]),
				});
		}
	}
}
